import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useFetchObjects from "../../api/useFetchObjects";
import useUpdate from "../../api/useUpdate";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
const IncomeEdit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/incomes", token);

  const { data: shopsData, isLoading: shopsLoading } = useFetchObjects(
    ["shops", "income-edit"],
    "shop/shop?page_size=all",
    token
  );
  const { data: currenciesData, isLoading: currenciesLoading } = useFetchObjects(
    ["currencies", "income-edit"],
    "shop/currencies?page_size=all",
    token
  );

  const shops = shopsData?.results || shopsData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedShopId, setSelectedShopId] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [incomeDate, setIncomeDate] = useState("");
  const [notes, setNotes] = useState("");
  const initialRateSelectionRef = useRef("");

  const shopCurrencies = useMemo(
    () => currencies.filter((cur) => String(cur.shop) === String(selectedShopId)),
    [currencies, selectedShopId]
  );

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "income-edit", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = accountsData?.results || accountsData || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );

  useEffect(() => {
    if (!editData) return;
    const currencyId = editData.currency_details?.id
      ? String(editData.currency_details.id)
      : editData.currency
        ? String(editData.currency)
        : "";
    const accountId = editData.account_details?.id
      ? String(editData.account_details.id)
      : editData.account
        ? String(editData.account)
        : "";
    const shopId = editData.shop ? String(editData.shop) : editData.currency_details?.shop ? String(editData.currency_details.shop) : "";
    setSelectedShopId(shopId);
    setSelectedCurrency(currencyId);
    setSelectedAccountId(accountId);
    setAmount(editData.amount || "");
    setExchangeRate(editData.exchange_rate || "");
    setIncomeDate(editData.income_date ? editData.income_date.slice(0, 16) : "");
    setNotes(editData.notes || "");
    initialRateSelectionRef.current = `${currencyId}:${accountId}`;
  }, [editData]);

  useEffect(() => {
    if (
      selectedCurrency &&
      !shopCurrencies.some((cur) => String(cur.id) === String(selectedCurrency))
    ) {
      setSelectedCurrency("");
    }
    if (
      selectedAccountId &&
      !accounts.some((acc) => String(acc.id) === String(selectedAccountId))
    ) {
      setSelectedAccountId(accounts[0] ? String(accounts[0].id) : "");
    }
  }, [selectedCurrency, selectedAccountId, accounts, shopCurrencies, selectedShopId]);

  const { data: directRateData } = useFetchObjects(
    ["income-edit-rate-direct", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["income-edit-rate-reverse", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );

  const exchangeRateOptions = useMemo(() => {
    if (!selectedCurrency) return [];
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) {
      return [{ value: "1", label: "1.000000 (same currency)" }];
    }

    const directRates = (directRateData?.results || []).map((rate) => ({
      value: String(rate.rate),
      label: `${rate.rate} (${rate.rate_date || "latest"})`,
    }));

    const reverseRates = (reverseRateData?.results || [])
      .filter((rate) => Number(rate.rate) > 0)
      .map((rate) => ({
        value: String(1 / Number(rate.rate)),
        label: `${(1 / Number(rate.rate)).toFixed(6)} (${rate.rate_date || "latest"})`,
      }));

    return [...directRates, ...reverseRates];
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    if (!editData || !exchangeRateOptions.length) return;
    const currentRateSelection = `${selectedCurrency}:${selectedAccountId}`;
    if (
      currentRateSelection !== initialRateSelectionRef.current ||
      !exchangeRateOptions.some((option) => option.value === String(exchangeRate))
    ) {
      setExchangeRate(exchangeRateOptions[0].value);
    }
  }, [editData, exchangeRateOptions, exchangeRate, selectedCurrency, selectedAccountId]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShopId);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    formData.append("income_date", incomeDate);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("income.editIncome")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            label={t("income.selectBranch")}
            fullWidth
            required
            value={selectedShopId}
            onChange={(e) => setSelectedShopId(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {shopsLoading ? (
              <MenuItem value=""><CircularProgress size={20} /></MenuItem>
            ) : (
              shops.map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("income.selectAccount")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedShopId || accountLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {accountLoading ? (
              <MenuItem value=""><CircularProgress size={20} /></MenuItem>
            ) : accounts.length === 0 ? (
              <MenuItem value="" disabled>{t("income.noAccount")}</MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("income.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            disabled={!selectedShopId}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {currenciesLoading ? (
              <MenuItem value=""><CircularProgress size={20} /></MenuItem>
            ) : (
              shopCurrencies.map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("income.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            select
            margin="dense"
            label={t("income.exchangeRate")}
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            disabled={!selectedCurrency || !selectedAccountId || exchangeRateOptions.length === 0}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {exchangeRateOptions.length === 0 ? (
              <MenuItem value="" disabled>
                {t("income.missingRate")}
              </MenuItem>
            ) : (
              exchangeRateOptions.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("income.convertedAmount")}
            fullWidth
            value={convertedAmount}
            disabled
            {...getRtlTextFieldProps(formDirection)}
          />

          {!exchangeRateOptions.length && selectedCurrency && selectedAccountId && (
            <Alert severity="warning" sx={{ mt: 1, textAlign: fieldTextAlign }}>
              {t("income.missingRate")}
            </Alert>
          )}

          <JalaliDateField mode="datetime"
            margin="dense"
            label={t("income.incomeDate")}
            fullWidth
            required
            value={incomeDate}
            onChange={(e) => setIncomeDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />

          <TextField
            margin="dense"
            label={t("income.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("income.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !selectedShopId ||
            !selectedCurrency ||
            !selectedAccountId ||
            !amount ||
            !exchangeRate ||
            !incomeDate
          }
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t("income.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncomeEdit;
